import { useEffect, useState } from "react";
import { publicApi } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles, Download, ChevronLeft, ChevronRight, ArrowRight,
  Award, ShieldCheck, Gem, Users, Globe2, HeartHandshake,
  Phone, Calendar, BookOpen, Mic, MapPin, CheckCircle2, Play
} from "lucide-react";
import CustomCursorUnused from "@/components/CustomCursor";
import LeadModal from "@/components/LeadModal";
import WhatsAppFloat from "@/components/WhatsAppFloat";

const SectionEyebrow = ({ children, gold = true }) => (
  <div className={`text-[10px] md:text-xs tracking-[0.5em] uppercase mb-4 font-poppins ${gold ? "text-[#B58A38]" : "text-[#6B1A1A]"}`}>
    <Sparkles size={12} className="inline mr-2 -mt-0.5" />
    {children}
  </div>
);

const GoldDivider = () => (
  <div className="mx-auto mt-6 flex items-center justify-center gap-2">
    <span className="h-px w-10 bg-gradient-to-r from-transparent to-[#B58A38]" />
    <span className="w-1.5 h-1.5 rotate-45 bg-[#B58A38]" />
    <span className="h-px w-10 bg-gradient-to-l from-transparent to-[#B58A38]" />
  </div>
);

const Nav = ({ onDownload, onTalk }) => (
  <header className="fixed top-0 inset-x-0 z-40 backdrop-blur-xl bg-[#FDFAF3]/85 border-b border-[#EAD9B5]/50">
    <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
      <Link to="/shravan" className="flex items-center gap-3" data-cursor="button">
        <div className="w-11 h-11 rounded-full border border-[#B58A38]/40 flex items-center justify-center text-[#6B1A1A] font-cinzel text-lg bg-[#F5EEDD]">ॐ</div>
        <div className="leading-tight">
          <div className="font-cinzel text-lg md:text-xl text-[#3B1C1C]">Rudralife</div>
          <div className="text-[9px] tracking-[0.35em] uppercase text-[#B58A38]">Silver Jubilee · 2026</div>
        </div>
      </Link>
      <nav className="hidden md:flex items-center gap-8 text-[11px] tracking-[0.25em] uppercase text-[#3B1C1C] font-poppins">
        <a href="#legacy" data-cursor="button" className="hover:text-[#B58A38] transition-colors">Legacy Mala</a>
        <a href="#mahapoojas" data-cursor="button" className="hover:text-[#B58A38] transition-colors">Mahapoojas</a>
        <a href="#kits" data-cursor="button" className="hover:text-[#B58A38] transition-colors">Power Kits</a>
        <a href="#timeline" data-cursor="button" className="hover:text-[#B58A38] transition-colors">25 Years</a>
        <a href="#exhibitions" data-cursor="button" className="hover:text-[#B58A38] transition-colors">Exhibitions</a>
      </nav>
      <div className="flex items-center gap-3">
        <button onClick={onTalk} data-testid="nav-talk-expert" data-cursor="button" className="hidden md:inline-flex text-[10px] tracking-[0.3em] uppercase border border-[#6B1A1A]/30 text-[#6B1A1A] px-4 py-2 rounded-full hover:bg-[#6B1A1A] hover:text-[#F5EEDD] transition-colors">
          Talk to Expert
        </button>
        <button onClick={onDownload} data-testid="nav-download-brochure" data-cursor="button" className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase bg-[#3B1C1C] hover:bg-[#6B1A1A] text-[#F5EEDD] px-4 py-2.5 rounded-full transition-colors">
          <Download size={12} /> Brochure
        </button>
      </div>
    </div>
  </header>
);

const Hero = ({ onCta1, onCta2, settings }) => {
  const [idx, setIdx] = useState(0);
  const banners = [
    {
      eyebrow: "25 Years of Divine Trust",
      title: "Silver Jubilee\nShravan Celebration",
      body: "Experience Rudralife's most exclusive spiritual offerings ever created.",
      cta: "Explore Shravan Offer",
      onCta: onCta1,
      bg: settings.shravan_hero_bg_1,
    },
    {
      eyebrow: "25th Anniversary Legacy Edition",
      title: "The Sacred Companion\nFor Your Life Journey",
      body: "Legacy Edition Siddha Mala — a limited creation for a limited few.",
      cta: "Download Exclusive Brochure",
      onCta: onCta2,
      bg: settings.shravan_hero_bg_2,
    },
  ];

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % banners.length), 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="relative pt-20 bg-[#FDFAF3]">
      <div className="relative w-full h-[92vh] min-h-[600px] overflow-hidden">
        {banners.map((b, i) => (
          <div key={i} className={`absolute inset-0 transition-opacity duration-[1400ms] ${i === idx ? "opacity-100" : "opacity-0"}`}>
            <img src={b.bg} alt="Shravan" className={`absolute inset-0 w-full h-full object-cover ${i === idx ? "kenburn" : ""}`} />
            <div className="absolute inset-0 bg-gradient-to-b from-[#3B1C1C]/70 via-[#3B1C1C]/40 to-[#FDFAF3]/95" />
            <div className="absolute inset-0 flex items-center">
              <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
                <div className="max-w-2xl fade-up">
                  <div className="inline-flex items-center gap-2 text-[10px] md:text-xs tracking-[0.5em] uppercase text-[#EBCF89] font-poppins mb-6">
                    <Sparkles size={12} /> {b.eyebrow}
                  </div>
                  <h1 className="font-cinzel text-[#FDFAF3] text-4xl sm:text-5xl md:text-7xl leading-[1.05] tracking-tight whitespace-pre-line">
                    {b.title}
                  </h1>
                  <p className="mt-6 text-[#F5EEDD]/85 text-base md:text-lg leading-relaxed font-poppins max-w-lg">
                    {b.body}
                  </p>
                  <div className="mt-8 flex flex-wrap gap-3">
                    <button
                      onClick={b.onCta}
                      data-cursor="button"
                      data-testid={`hero-cta-${i}`}
                      className="group inline-flex items-center gap-3 px-7 py-4 rounded-full bg-gradient-to-r from-[#B58A38] via-[#EBCF89] to-[#B58A38] text-[#3B1C1C] font-poppins text-[11px] tracking-[0.35em] uppercase hover:shadow-[0_15px_40px_rgba(181,138,56,0.5)] transition-all"
                    >
                      {b.cta}
                      <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        <button
          onClick={() => setIdx((idx - 1 + banners.length) % banners.length)}
          data-cursor="button"
          aria-label="Previous"
          className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-[#FDFAF3]/15 backdrop-blur-md border border-[#F5EEDD]/25 flex items-center justify-center text-[#F5EEDD] hover:bg-[#B58A38] transition-colors"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          onClick={() => setIdx((idx + 1) % banners.length)}
          data-cursor="button"
          aria-label="Next"
          className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-[#FDFAF3]/15 backdrop-blur-md border border-[#F5EEDD]/25 flex items-center justify-center text-[#F5EEDD] hover:bg-[#B58A38] transition-colors"
        >
          <ChevronRight size={20} />
        </button>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
          {banners.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              data-cursor="button"
              aria-label={`Banner ${i + 1}`}
              className={`h-[3px] rounded-full transition-all ${i === idx ? "w-14 bg-[#EBCF89]" : "w-4 bg-[#F5EEDD]/40"}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const LegacySection = ({ onDownload, image }) => (
  <section id="legacy" className="relative bg-[#FDFAF3] py-24 md:py-36 overflow-hidden">
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-14 md:gap-20 items-center">
      <div className="relative order-2 md:order-1">
        <div className="absolute -inset-6 md:-inset-10 rounded-[2rem] bg-gradient-to-br from-[#B58A38]/12 via-transparent to-[#6B1A1A]/8 blur-2xl" />
        <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden bg-[#F5EEDD] border border-[#B58A38]/20">
          <img src={image} alt="Legacy Siddha Mala" className="w-full h-full object-cover" />
          <div className="absolute top-6 left-6 flex flex-col items-start gap-2">
            <div className="px-3 py-1 rounded-full bg-[#3B1C1C]/85 backdrop-blur text-[#EBCF89] text-[9px] tracking-[0.4em] uppercase">Legacy Edition</div>
            <div className="px-3 py-1 rounded-full bg-[#B58A38] text-[#3B1C1C] text-[9px] tracking-[0.35em] uppercase font-semibold">Limited Creation</div>
          </div>
        </div>
      </div>
      <div className="order-1 md:order-2">
        <SectionEyebrow>Silver Jubilee Exclusive</SectionEyebrow>
        <h2 className="font-cinzel text-3xl md:text-5xl leading-[1.15] text-[#3B1C1C]">
          25th Anniversary<br />
          Silver Jubilee<br />
          <span className="italic text-[#B58A38]">Legacy Edition</span><br />
          Siddha Mala
        </h2>
        <div className="mt-8 space-y-3 text-[#5C4C40] font-poppins text-base md:text-lg leading-relaxed">
          <p className="font-medium text-[#3B1C1C]">The World&apos;s Most Powerful Rudraksha Mala.</p>
          <p>Crafted only for a limited number of seekers this Shravan.</p>
        </div>
        <div className="mt-10">
          <button
            onClick={onDownload}
            data-testid="legacy-download-btn"
            data-cursor="button"
            className="group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-gradient-to-r from-[#6B1A1A] via-[#8B2323] to-[#6B1A1A] text-[#F5EEDD] text-[11px] tracking-[0.35em] uppercase hover:shadow-[0_20px_40px_rgba(107,26,26,0.4)] transition-all"
          >
            <Download size={14} /> Download Complete Offer PDF
            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </button>
          <p className="mt-4 text-[11px] text-[#7A6A5C] font-poppins">
            Personally curated by our founder — every download connects you to a dedicated expert.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const MahapoojasSection = ({ items, onRegister }) => (
  <section id="mahapoojas" className="bg-[#F5EEDD] py-24 md:py-32 relative">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="text-center max-w-3xl mx-auto mb-14 md:mb-20">
        <SectionEyebrow>Six Divine Rituals</SectionEyebrow>
        <h2 className="font-cinzel text-3xl md:text-5xl text-[#3B1C1C] leading-tight">
          Six Mahapoojas<br />For Your Family&apos;s Overall Blessings
        </h2>
        <p className="mt-5 font-poppins text-[#5C4C40] text-sm md:text-base leading-relaxed">
          Participate in the most auspicious Shravan rituals and receive energized Prasad at your home.
        </p>
        <GoldDivider />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
        {items.map((p) => (
          <div key={p.id} className="group relative bg-[#FDFAF3] rounded-3xl overflow-hidden border border-[#EAD9B5]/50 hover:border-[#B58A38]/50 hover:shadow-[0_30px_60px_rgba(59,28,28,0.12)] transition-all hover:-translate-y-1">
            <div className="aspect-[4/3] overflow-hidden bg-[#F5EEDD] relative">
              <img src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#3B1C1C]/60 to-transparent" />
              <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-[#3B1C1C]/85 backdrop-blur text-[#EBCF89] text-[9px] tracking-[0.35em] uppercase">
                {p.date}
              </div>
            </div>
            <div className="p-7">
              <h3 className="font-cinzel text-xl md:text-2xl text-[#3B1C1C]">{p.name}</h3>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="text-[9px] tracking-[0.35em] uppercase text-[#B58A38] font-poppins">Blessing</span>
                <span className="h-px flex-1 bg-[#EAD9B5]" />
              </div>
              <p className="mt-2 font-cinzel text-lg font-semibold text-[#6B1A1A] leading-snug">{p.benefit}</p>
              <button
                onClick={() => onRegister(p)}
                data-cursor="button"
                data-testid={`mahapooja-btn-${p.id}`}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 py-3 rounded-full border border-[#6B1A1A]/30 text-[#6B1A1A] hover:bg-[#6B1A1A] hover:text-[#F5EEDD] text-[10px] tracking-[0.35em] uppercase transition-colors"
              >
                Participate Now <ArrowRight size={12} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const KitsSection = ({ items, onExplore }) => (
  <section id="kits" className="bg-[#FDFAF3] py-24 md:py-32">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="text-center max-w-3xl mx-auto mb-14 md:mb-20">
        <SectionEyebrow>Divine Rudraksha Power Kits</SectionEyebrow>
        <h2 className="font-cinzel text-3xl md:text-5xl text-[#3B1C1C] leading-tight">
          Choose By Life Goal,<br />Not By Product
        </h2>
        <p className="mt-5 font-poppins text-[#5C4C40] text-sm md:text-base">
          Discover the Rudraksha combination that mirrors the life you are creating.
        </p>
        <GoldDivider />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
        {items.map((k) => (
          <div key={k.id} className="group relative rounded-3xl overflow-hidden border border-[#EAD9B5]/60 hover:border-[#B58A38]/50 bg-[#FDFAF3] transition-all hover:-translate-y-1 hover:shadow-[0_25px_50px_rgba(59,28,28,0.1)]">
            <div className="aspect-[4/5] relative overflow-hidden">
              <img src={k.image} alt={k.goal} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#3B1C1C]/85 via-[#3B1C1C]/25 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-5 text-[#F5EEDD]">
                <div className="text-[9px] tracking-[0.4em] uppercase text-[#EBCF89]">Life Goal</div>
                <div className="font-cinzel text-xl md:text-2xl mt-1 leading-tight">{k.goal}</div>
                {k.result && <div className="font-cinzel italic text-sm text-[#EBCF89] mt-2">{k.result}</div>}
              </div>
            </div>
            <div className="p-5 bg-[#FDFAF3]">
              <p className="text-xs text-[#5C4C40] font-poppins leading-relaxed line-clamp-2 min-h-[2.5rem]">{k.description}</p>
              <button
                onClick={() => onExplore(k)}
                data-testid={`kit-explore-${k.id}`}
                data-cursor="button"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-full border border-[#B58A38]/40 text-[#6B1A1A] hover:bg-[#B58A38] hover:text-[#3B1C1C] text-[10px] tracking-[0.35em] uppercase transition-colors"
              >
                Explore Kit <ArrowRight size={12} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const TimelineSection = ({ milestones, settings }) => (
  <section id="timeline" className="relative bg-[#EFE4CE] py-24 md:py-32 overflow-hidden">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="text-center max-w-3xl mx-auto mb-16">
        <SectionEyebrow>Three Generations · Unbroken Trust</SectionEyebrow>
        <h2 className="font-cinzel text-3xl md:text-5xl text-[#3B1C1C]">
          25 Years of <span className="italic text-[#B58A38]">Rudralife</span>
        </h2>
        <p className="mt-5 font-poppins text-[#5C4C40] text-sm md:text-base">
          A quarter century of authenticity, research and sacred service.
        </p>
        <GoldDivider />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 mb-16">
        {[
          { n: settings.stat_seekers || "5,00,000+", l: "Seekers Served" },
          { n: settings.stat_exhibitions || "1,200+", l: "Global Exhibitions" },
          { n: settings.stat_countries || "5", l: "Countries" },
          { n: settings.stat_years || "25+", l: "Years of Trust" },
        ].map((s) => (
          <div key={s.l} className="text-center">
            <div className="font-cinzel text-3xl md:text-5xl text-[#6B1A1A]">{s.n}</div>
            <div className="text-[9px] md:text-[10px] tracking-[0.35em] uppercase text-[#7A6A5C] font-poppins mt-2">{s.l}</div>
          </div>
        ))}
      </div>

      <div className="relative">
        <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[#B58A38] to-transparent" />
        <div className="space-y-8 md:space-y-14">
          {milestones.map((m, i) => (
            <div key={m.id} className={`md:grid md:grid-cols-2 md:gap-16 items-center ${i % 2 ? "" : "md:[&>*:first-child]:order-2"}`}>
              <div className={`hidden md:block ${i % 2 ? "text-left pr-16" : "text-right pl-16"}`}>
                <div className="font-cinzel text-4xl md:text-6xl text-[#B58A38] italic">{m.year}</div>
              </div>
              <div className={`bg-[#FDFAF3] rounded-3xl p-6 md:p-8 border border-[#EAD9B5] relative ${i % 2 ? "md:ml-16" : "md:mr-16"}`}>
                <div className="md:hidden font-cinzel text-3xl text-[#B58A38] italic mb-2">{m.year}</div>
                <h3 className="font-cinzel text-xl md:text-2xl text-[#3B1C1C]">{m.title}</h3>
                <p className="mt-3 font-poppins text-sm text-[#5C4C40] leading-relaxed">{m.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const AceAuraSection = ({ onTalk }) => (
  <section className="relative py-24 md:py-32 bg-[#FDFAF3] overflow-hidden">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="text-center max-w-3xl mx-auto mb-14">
        <SectionEyebrow>Rudralife Innovation</SectionEyebrow>
        <h2 className="font-cinzel text-3xl md:text-5xl text-[#3B1C1C]">
          ACE — Aura Certified <span className="italic text-[#B58A38]">Energization</span>
        </h2>
        <p className="mt-5 font-poppins text-[#5C4C40] text-sm md:text-base">
          A breakthrough process combining ancient Vedic ritual with modern aura documentation.
        </p>
        <GoldDivider />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
        <div className="grid grid-cols-2 gap-4">
          {[
            { n: "1,25,000", l: "Panchakshari Mantras" },
            { n: "72 Hrs", l: "Continuous Energization" },
            { n: "Aura", l: "Scientifically Documented" },
            { n: "Certified", l: "Every Single Bead" },
          ].map((f) => (
            <div key={f.l} className="rounded-3xl bg-[#F5EEDD] border border-[#EAD9B5] p-6 md:p-7">
              <div className="font-cinzel text-2xl md:text-3xl text-[#6B1A1A]">{f.n}</div>
              <div className="text-[10px] tracking-[0.35em] uppercase text-[#7A6A5C] font-poppins mt-2">{f.l}</div>
            </div>
          ))}
        </div>

        <div className="rounded-3xl bg-gradient-to-br from-[#3B1C1C] via-[#5C2828] to-[#3B1C1C] p-8 md:p-10 text-[#F5EEDD] relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#B58A38]/20 blur-3xl" />
          <div className="relative">
            <div className="text-[9px] tracking-[0.5em] uppercase text-[#EBCF89]">Before → After</div>
            <h3 className="font-cinzel text-2xl md:text-3xl mt-3 leading-tight">
              Documented Aura Transformation
            </h3>
            <p className="mt-4 text-[#F5EEDD]/80 font-poppins text-sm leading-relaxed">
              Every Rudralife Rudraksha undergoes ACE — a proprietary 72-hour energization protocol chanting the sacred Panchakshari Mantra 1,25,000 times, with pre and post aura documentation.
            </p>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <div className="rounded-2xl overflow-hidden aspect-square bg-[#5C2828] border border-[#B58A38]/25 flex items-center justify-center">
                <div className="text-center px-2">
                  <div className="text-[9px] tracking-[0.4em] uppercase text-[#EBCF89]">Before</div>
                  <div className="font-cinzel text-2xl mt-2 text-[#F5EEDD]/70">Scattered</div>
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden aspect-square bg-gradient-to-br from-[#B58A38] to-[#EBCF89] flex items-center justify-center">
                <div className="text-center text-[#3B1C1C]">
                  <div className="text-[9px] tracking-[0.4em] uppercase">After</div>
                  <div className="font-cinzel text-2xl mt-2">Aligned</div>
                </div>
              </div>
            </div>
            <button
              onClick={onTalk}
              data-cursor="button"
              data-testid="ace-talk-btn"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 py-3 rounded-full bg-[#EBCF89] text-[#3B1C1C] text-[10px] tracking-[0.35em] uppercase hover:bg-[#F5EEDD] transition-colors"
            >
              Talk to an Expert <ArrowRight size={12} />
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const REASONS = [
  { icon: Award, t: "25 Years Experience" },
  { icon: Gem, t: "Authentic Rudraksha" },
  { icon: ShieldCheck, t: "Certified Beads" },
  { icon: BookOpen, t: "Vedic Experts" },
  { icon: Users, t: "Millions of Customers" },
  { icon: Globe2, t: "Worldwide Shipping" },
  { icon: Phone, t: "Personal Consultation" },
  { icon: HeartHandshake, t: "Handcrafted Siddha Malas" },
];

const WhyRudralife = () => (
  <section className="bg-[#F5EEDD] py-24 md:py-32">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="text-center max-w-3xl mx-auto mb-14">
        <SectionEyebrow>Why Rudralife</SectionEyebrow>
        <h2 className="font-cinzel text-3xl md:text-5xl text-[#3B1C1C]">
          Eight Reasons <span className="italic text-[#B58A38]">Seekers</span> Choose Us
        </h2>
        <GoldDivider />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        {REASONS.map((r) => {
          const Icon = r.icon;
          return (
            <div key={r.t} className="group text-center p-6 md:p-8 rounded-2xl bg-[#FDFAF3] border border-[#EAD9B5]/70 hover:border-[#B58A38]/50 hover:shadow-[0_20px_40px_rgba(59,28,28,0.08)] transition-all hover:-translate-y-1">
              <div className="w-14 h-14 mx-auto rounded-full bg-[#F5EEDD] border border-[#B58A38]/30 flex items-center justify-center text-[#6B1A1A] group-hover:bg-[#B58A38] group-hover:text-[#3B1C1C] transition-colors">
                <Icon size={20} />
              </div>
              <div className="mt-4 font-cinzel text-sm md:text-base text-[#3B1C1C] leading-tight">{r.t}</div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

const ExhibitionsSection = ({ items, onRegister }) => (
  <section id="exhibitions" className="bg-[#FDFAF3] py-24 md:py-32">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="text-center max-w-3xl mx-auto mb-14">
        <SectionEyebrow>Meet Us In Person</SectionEyebrow>
        <h2 className="font-cinzel text-3xl md:text-5xl text-[#3B1C1C]">
          Upcoming <span className="italic text-[#B58A38]">Exhibitions</span>
        </h2>
        <GoldDivider />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        {items.map((e) => (
          <div key={e.id} className="group relative rounded-3xl overflow-hidden border border-[#EAD9B5]/70 bg-[#F5EEDD] hover:shadow-[0_25px_50px_rgba(59,28,28,0.15)] transition-all hover:-translate-y-1">
            <div className="aspect-[4/5] relative">
              {e.image ? (
                <img src={e.image} alt={e.city} className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-[#6B1A1A] via-[#8B2323] to-[#3B1C1C]" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-[#3B1C1C]/90 via-[#3B1C1C]/30 to-transparent" />
              {e.status === "live" && (
                <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#B58A38]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#3B1C1C] animate-pulse" />
                  <span className="text-[9px] tracking-[0.3em] uppercase text-[#3B1C1C] font-semibold">Live Now</span>
                </div>
              )}
              <div className="absolute inset-x-0 bottom-0 p-5 text-[#F5EEDD]">
                <div className="font-cinzel italic text-2xl md:text-3xl text-[#EBCF89]">{e.city}</div>
                {e.venue && <div className="mt-2 text-xs text-[#F5EEDD]/80 line-clamp-1 flex items-center gap-1"><MapPin size={11} /> {e.venue}</div>}
                {e.date_range && <div className="mt-1 text-[10px] tracking-widest uppercase text-[#EBCF89] flex items-center gap-1"><Calendar size={11} /> {e.date_range}</div>}
                <button
                  onClick={() => onRegister(e)}
                  data-testid={`exhib-register-${e.id}`}
                  data-cursor="button"
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-full bg-[#EBCF89] text-[#3B1C1C] text-[10px] tracking-[0.35em] uppercase hover:bg-[#F5EEDD] transition-colors"
                >
                  Register <ArrowRight size={12} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const BlogsSection = ({ items }) => (
  <section id="blogs" className="bg-[#EFE4CE] py-24 md:py-32">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="text-center max-w-3xl mx-auto mb-14">
        <SectionEyebrow>Educate Before Selling</SectionEyebrow>
        <h2 className="font-cinzel text-3xl md:text-5xl text-[#3B1C1C]">
          Latest <span className="italic text-[#B58A38]">Shravan</span> Articles
        </h2>
        <GoldDivider />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
        {items.map((b) => (
          <a
            key={b.id}
            href={b.link || "#"}
            target={b.link ? "_blank" : undefined}
            rel="noreferrer"
            data-cursor="button"
            className="group block bg-[#FDFAF3] rounded-3xl overflow-hidden border border-[#EAD9B5]/70 hover:border-[#B58A38]/50 hover:shadow-[0_20px_40px_rgba(59,28,28,0.1)] transition-all hover:-translate-y-1"
          >
            <div className="aspect-[16/10] overflow-hidden">
              <img src={b.image} alt={b.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            </div>
            <div className="p-6">
              <div className="flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase text-[#B58A38] font-poppins mb-3">
                <span>{b.category}</span>
                <span>·</span>
                <span>{b.date}</span>
              </div>
              <h3 className="font-cinzel text-lg md:text-xl text-[#3B1C1C] leading-snug line-clamp-2 min-h-[3rem]">{b.title}</h3>
              <p className="mt-3 text-xs text-[#5C4C40] font-poppins leading-relaxed line-clamp-3">{b.excerpt}</p>
              <div className="mt-4 inline-flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase text-[#6B1A1A] group-hover:text-[#B58A38] transition-colors">
                Read More <ArrowRight size={12} />
              </div>
            </div>
          </a>
        ))}
      </div>
    </div>
  </section>
);

const PodcastSection = () => (
  <section className="bg-[#FDFAF3] py-24 md:py-32">
    <div className="max-w-6xl mx-auto px-6 md:px-12">
      <div className="rounded-[2rem] bg-gradient-to-br from-[#3B1C1C] via-[#5C2828] to-[#3B1C1C] p-8 md:p-16 text-[#F5EEDD] relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#B58A38]/15 blur-3xl" />
        <div className="relative grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#B58A38]/20 border border-[#B58A38]/30 text-[#EBCF89] text-[9px] tracking-[0.5em] uppercase font-poppins">
              <Mic size={11} /> Coming Soon
            </div>
            <h2 className="font-cinzel text-3xl md:text-5xl mt-5 leading-tight">
              Tanay Seetha<br /><span className="italic text-[#EBCF89]">Podcast</span>
            </h2>
            <p className="mt-5 font-poppins text-[#F5EEDD]/80 text-sm md:text-base leading-relaxed max-w-md">
              Deep conversations with spiritual masters, scientists and seekers — the untold stories behind Rudralife&apos;s 25-year journey.
            </p>
            <button
              data-cursor="button"
              className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#EBCF89] text-[#3B1C1C] text-[10px] tracking-[0.35em] uppercase hover:bg-[#F5EEDD] transition-colors"
            >
              Notify Me on Launch <ArrowRight size={12} />
            </button>
          </div>
          <div className="relative aspect-square rounded-3xl bg-[#5C2828] border border-[#B58A38]/25 overflow-hidden flex items-center justify-center">
            <div className="w-24 h-24 rounded-full bg-[#B58A38] flex items-center justify-center text-[#3B1C1C]">
              <Play size={30} fill="currentColor" />
            </div>
            <div className="absolute inset-x-0 bottom-6 text-center">
              <div className="font-cinzel italic text-xl text-[#EBCF89]">Episode 01</div>
              <div className="text-[9px] tracking-[0.3em] uppercase text-[#F5EEDD]/60 mt-1">Coming this Shravan</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const FAQSection = ({ faqs, onTalk }) => {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="bg-[#F5EEDD] py-24 md:py-32">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <div className="text-center mb-14">
          <SectionEyebrow>Seeker&apos;s Questions</SectionEyebrow>
          <h2 className="font-cinzel text-3xl md:text-5xl text-[#3B1C1C]">
            Frequently Asked <span className="italic text-[#B58A38]">Questions</span>
          </h2>
          <GoldDivider />
        </div>

        <div className="space-y-3">
          {faqs.map((f, i) => (
            <div key={i} className="bg-[#FDFAF3] rounded-2xl border border-[#EAD9B5]/70 overflow-hidden">
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                data-cursor="button"
                data-testid={`shravan-faq-${i}`}
                className="w-full flex items-start justify-between text-left px-6 md:px-8 py-5 hover:bg-[#F5EEDD]/50 transition-colors"
              >
                <span className="font-cinzel text-base md:text-lg text-[#3B1C1C] pr-6 leading-tight">{f.q}</span>
                <span className={`text-[#B58A38] text-xl leading-none transition-transform ${open === i ? "rotate-45" : ""}`}>+</span>
              </button>
              {open === i && (
                <div className="px-6 md:px-8 pb-6 text-sm text-[#5C4C40] font-poppins leading-relaxed">
                  {f.a}
                  <button
                    onClick={onTalk}
                    data-cursor="button"
                    className="mt-4 inline-flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase text-[#6B1A1A] border-b border-[#6B1A1A]/30 pb-1 hover:text-[#B58A38] hover:border-[#B58A38] transition-colors"
                  >
                    Connect with Rudralife <ArrowRight size={11} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const FinalCTA = ({ onDownload, onTalk, onExhibition }) => (
  <section className="relative py-24 md:py-32 bg-[#FDFAF3] overflow-hidden">
    <div className="max-w-5xl mx-auto px-6 md:px-12 text-center relative">
      <div className="absolute inset-0 -z-10 flex items-center justify-center">
        <div className="w-[600px] h-[600px] rounded-full bg-gradient-to-br from-[#B58A38]/15 via-transparent to-[#6B1A1A]/10 blur-3xl" />
      </div>
      <SectionEyebrow>The Silver Jubilee Invitation</SectionEyebrow>
      <h2 className="font-cinzel text-3xl md:text-6xl text-[#3B1C1C] leading-[1.1]">
        One Lifetime.<br />
        <span className="italic text-[#B58A38]">One Sacred Opportunity.</span>
      </h2>
      <p className="mt-8 font-poppins text-[#5C4C40] text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
        Celebrate Rudralife&apos;s 25th Silver Jubilee with the most powerful spiritual offerings ever created.
      </p>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto text-left">
        {[
          "Limited Siddha Mala creations",
          "Limited Shravan Mahapooja participation",
          "Limited Anniversary Benefits",
        ].map((l) => (
          <div key={l} className="flex items-start gap-2 text-xs md:text-sm text-[#5C4C40] font-poppins">
            <CheckCircle2 size={16} className="text-[#B58A38] mt-0.5 flex-shrink-0" />
            {l}
          </div>
        ))}
      </div>

      <div className="mt-12 flex flex-col md:flex-row items-center justify-center gap-3">
        <button
          onClick={onDownload}
          data-testid="final-download-brochure"
          data-cursor="button"
          className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-[#B58A38] via-[#EBCF89] to-[#B58A38] text-[#3B1C1C] text-[11px] tracking-[0.35em] uppercase hover:shadow-[0_20px_40px_rgba(181,138,56,0.4)] transition-all"
        >
          <Download size={14} /> Download Brochure
        </button>
        <button
          onClick={onTalk}
          data-testid="final-talk-expert"
          data-cursor="button"
          className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#3B1C1C] hover:bg-[#6B1A1A] text-[#F5EEDD] text-[11px] tracking-[0.35em] uppercase transition-colors"
        >
          <Phone size={14} /> Talk to Expert
        </button>
        <button
          onClick={onExhibition}
          data-testid="final-register-exhibition"
          data-cursor="button"
          className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border-2 border-[#6B1A1A]/30 text-[#6B1A1A] hover:bg-[#6B1A1A] hover:text-[#F5EEDD] text-[11px] tracking-[0.35em] uppercase transition-colors"
        >
          <Calendar size={14} /> Register For Exhibition
        </button>
      </div>
    </div>
  </section>
);

const ShravanFooter = ({ settings }) => (
  <footer className="bg-[#3B1C1C] text-[#F5EEDD]/85 py-16">
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-8">
      <div className="md:col-span-2">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-[#5C2828] border border-[#B58A38]/40 flex items-center justify-center font-cinzel text-lg text-[#EBCF89]">ॐ</div>
          <div>
            <div className="font-cinzel text-2xl text-[#F5EEDD]">Rudralife</div>
            <div className="text-[9px] tracking-[0.4em] uppercase text-[#EBCF89] mt-1">Silver Jubilee · 2001-2026</div>
          </div>
        </div>
        <p className="mt-6 max-w-md text-sm font-poppins leading-relaxed">
          25 years of unbroken sacred service. Three generations. Five continents. One quiet devotion — the sacred Rudraksha.
        </p>
      </div>
      <div>
        <div className="text-[10px] tracking-[0.35em] uppercase text-[#EBCF89] font-poppins mb-4">Visit Us</div>
        <p className="text-sm font-poppins leading-relaxed">{settings.address}</p>
        <p className="mt-3 text-xs font-poppins">{settings.open_days} · {settings.open_time}</p>
      </div>
      <div>
        <div className="text-[10px] tracking-[0.35em] uppercase text-[#EBCF89] font-poppins mb-4">Contact</div>
        <a href={`tel:${settings.phone}`} className="block text-sm font-poppins hover:text-[#EBCF89]">{settings.phone}</a>
        <a href={`mailto:${settings.email}`} className="block text-sm font-poppins hover:text-[#EBCF89] mt-1">{settings.email}</a>
      </div>
    </div>
    <div className="max-w-7xl mx-auto px-6 md:px-12 mt-12 pt-6 border-t border-[#5C2828] flex flex-col md:flex-row items-center justify-between text-[10px] tracking-[0.3em] uppercase text-[#F5EEDD]/50">
      <div>© {new Date().getFullYear()} Rudralife. Silver Jubilee Edition.</div>
      <div className="mt-3 md:mt-0">Crafted with devotion · ॐ नमः शिवाय</div>
    </div>
  </footer>
);

const Shravan = () => {
  const [d, setD] = useState({ settings: {}, mahapoojas: [], kits: [], milestones: [], exhibitions: [], blogs: [] });
  const [modal, setModal] = useState(null); // { interest, title, subtitle, ctaLabel }

  useEffect(() => {
    (async () => {
      try {
        const [settings, mahapoojas, kits, milestones, exhibitions, blogs] = await Promise.all([
          publicApi.settings(),
          publicApi.list("shravan_pujas"),
          publicApi.list("power_kits"),
          publicApi.list("milestones"),
          publicApi.list("exhibitions"),
          publicApi.list("blogs"),
        ]);
        setD({ settings, mahapoojas, kits, milestones, exhibitions, blogs });
      } catch (e) {
        console.error(e);
        toast.error("Failed to load content");
      }
    })();
  }, []);

  const openBrochure = () => setModal({
    interest: "brochure",
    title: "Download the Silver Jubilee Brochure",
    subtitle: "The Legacy Edition Siddha Mala PDF — sent instantly to your inbox.",
    ctaLabel: "Download Now",
  });
  const openTalk = () => setModal({
    interest: "consultation",
    title: "Talk to a Rudralife Expert",
    subtitle: "Your first consultation is complimentary. We reach out within 24 hours.",
    ctaLabel: "Request Consultation",
  });
  const openExhibition = (e) => setModal({
    interest: e?.city ? `exhibition:${e.city}` : "exhibition",
    title: e?.city ? `Register for ${e.city} Exhibition` : "Register For Exhibition",
    subtitle: e?.date_range ? `${e.date_range} · ${e.venue || ""}` : "Reserve your slot for our upcoming Rudraksha exhibitions.",
    ctaLabel: "Reserve Slot",
  });
  const openMahapooja = (p) => setModal({
    interest: `mahapooja:${p.name}`,
    title: `Participate in ${p.name}`,
    subtitle: `${p.date} · ${p.benefit}`,
    ctaLabel: "Register Sankalp",
  });
  const openKit = (k) => setModal({
    interest: `kit:${k.goal}`,
    title: `Explore Your ${k.goal} Kit`,
    subtitle: "Our expert curates a Rudraksha combination personally suited to your goal.",
    ctaLabel: "Get My Kit Recommendation",
  });

  return (
    <div className="bg-[#FDFAF3] text-[#3B1C1C] font-poppins">
      <Nav onDownload={openBrochure} onTalk={openTalk} />

      <Hero settings={d.settings} onCta1={openBrochure} onCta2={openBrochure} />
      <LegacySection onDownload={openBrochure} image={d.settings.shravan_legacy_image} />
      <MahapoojasSection items={d.mahapoojas} onRegister={openMahapooja} />
      <KitsSection items={d.kits} onExplore={openKit} />
      <TimelineSection milestones={d.milestones} settings={d.settings} />
      <AceAuraSection onTalk={openTalk} />
      <WhyRudralife />
      <ExhibitionsSection items={d.exhibitions.slice(0, 8)} onRegister={openExhibition} />
      <BlogsSection items={d.blogs} />
      <PodcastSection />
      <FAQSection faqs={d.settings.faqs || []} onTalk={openTalk} />
      <FinalCTA onDownload={openBrochure} onTalk={openTalk} onExhibition={() => openExhibition()} />
      <ShravanFooter settings={d.settings} />

      <WhatsAppFloat number={d.settings.whatsapp_number} />
      <LeadModal open={!!modal} onClose={() => setModal(null)} {...(modal || {})} />
    </div>
  );
};

export default Shravan;
